import React, { useEffect, useMemo, useRef, useState } from "react";
import { GoldSilver } from "../DesignSystem/GoldSilver";
import { User } from "../models/users";
import { Vote, VoteJSON } from "../models/vote";
import { Entry } from "../models/entries";
import { Compo } from "../models/compos";
import { api } from "../api";
import { CompoCategoriesClient } from "../../../src/entity/CompoCategoriesClient";
import { markdown } from "markdown";
import { Prompt } from "react-router";
import { extractPageX } from "./voteUtils";
import { SmallButton } from "../DesignSystem/SmallButton";
import { UserSearchInput } from "../DesignSystem/UserSearchInput";
import { TestIds } from "../testIds";

export type VoteWidgetProps = {
  initialScore: number | undefined;
  maxScore: number;
  category: string;
  category_id: number;
  inputref: (input: HTMLInputElement) => void;
  mouseOverCategory: (cat: string) => void;
};

export const VoteWidget = ({
  initialScore,
  maxScore,
  category,
  category_id,
  inputref,
  mouseOverCategory,
}: VoteWidgetProps): JSX.Element => {
  const [score, setScore] = useState<number | undefined>(initialScore);
  const [mouseOveredScore, setMouseOveredScore] = useState<number | undefined>(
    undefined
  );

  const scoreToShow = mouseOveredScore !== undefined ? mouseOveredScore : score;

  const stars = useMemo(() => {
    const starElements: JSX.Element[] = [];
    for (let i = 1; i <= maxScore; i++) {
      let starType =
        scoreToShow !== undefined && i <= scoreToShow ? "fa-star" : "fa-star-o";

      if (scoreToShow !== undefined && scoreToShow - i + 1 === 0.5) {
        starType = "fa-star-half-o";
      }

      starElements.push(
        <i
          key={i}
          className={`fa ${starType}`}
          onMouseOver={(e) => mouseOver(e, i)}
          onMouseMove={(e) => mouseOver(e, i)}
          onMouseOut={(e) => mouseOut(e, i)}
          onTouchEnd={(e) => mouseOut(e, i)}
          onMouseDown={(e) => mouseDown(e, i)}
          onTouchStart={(e) => mouseDown(e, i)}
          aria-hidden="true"
        ></i>
      );
    }
    return starElements;
  }, [maxScore, scoreToShow]);

  function showExplanation(): void {
    mouseOverCategory(category.toLowerCase());
  }

  function hideExplanation(): void {
    // Don't clear - keep the last hovered category visible
  }

  function mouseOver(
    e: React.MouseEvent<HTMLElement>,
    nextScore: number
  ): void {
    const nativeEvent =
      "nativeEvent" in e ? (e.nativeEvent as MouseEvent | TouchEvent) : e;
    const event = (
      "touches" in nativeEvent && nativeEvent.touches.length > 0
        ? nativeEvent.touches[0]
        : nativeEvent
    ) as MouseEvent | Touch;
    const relativeMouseX =
      (event.pageX - e.currentTarget.offsetLeft) / e.currentTarget.offsetWidth;
    const half = relativeMouseX < 0.5;

    showExplanation();
    setMouseOveredScore(Math.max(nextScore - (half ? 0.5 : 0.0), 0));
  }

  function mouseOut(
    _e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>,
    _score: number
  ): void {
    hideExplanation();
    setMouseOveredScore(undefined);
  }

  function mouseDown(
    e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>,
    nextScore: number
  ): void {
    const pageX = extractPageX(e.nativeEvent);
    const relativeMouseX =
      (pageX - e.currentTarget.offsetLeft) / e.currentTarget.offsetWidth;
    const half = relativeMouseX < 0.5;
    const finalScore = Math.max(nextScore - (half ? 0.5 : 0.0), 0);
    setScore(finalScore);
  }

  return (
    <div className="flex items-center gap-3 py-1">
      <span className="w-32 text-sm font-medium text-slate-700 dark:text-slate-300">
        {category}
      </span>
      <div className="flex items-center gap-1">
        <span
          onMouseOver={(e) => mouseOver(e, 0)}
          onMouseOut={(e) => mouseOut(e, 0)}
          onMouseDown={(e) => mouseDown(e, 0)}
          onTouchStart={(e) => mouseDown(e, 0)}
          onTouchEnd={(e) => mouseOut(e, 0)}
          className="cursor-pointer text-lg text-slate-400 transition-colors hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
        >
          <i
            className={
              "fa fa-times-circle" +
              (scoreToShow === 0 ? " text-amber-500" : "-o")
            }
            aria-hidden="true"
          />
        </span>
        <span className="flex cursor-pointer gap-0.5 text-lg text-amber-400">
          {stars}
        </span>
      </div>
      {score !== undefined && (
        <span className="min-w-[3rem] text-sm font-medium text-slate-600 dark:text-slate-300">
          {score} <i className="fa fa-star text-amber-400" aria-hidden="true" />
        </span>
      )}
      <input
        type="hidden"
        name={`category_${category_id}`}
        ref={(r) => r && inputref(r)}
        value={score ?? ""}
        readOnly
      />
    </div>
  );
};

export type JudgeOneSongProps = {
  entry: Entry;
  compo: Compo;
  categories: CompoCategoriesClient[];
  bounty: number;
  user?: User;
  vote: Vote | undefined;
  onVote?: (vote: VoteJSON) => void;
};

export const JudgeOneSong = ({
  entry,
  compo,
  categories,
  bounty,
  user,
  vote,
  onVote,
}: JudgeOneSongProps): JSX.Element => {
  const reviewRef = useRef<HTMLTextAreaElement | null>(null);
  const publicRef = useRef<HTMLInputElement | null>(null);
  const votesRef = useRef<HTMLInputElement[]>([]);

  const reviewStorageKey = `review:${entry.id}`;

  const getInitialReviewText = (): string => {
    const raw = localStorage.getItem(reviewStorageKey);
    if (!raw) return vote?.review ?? "";

    try {
      const cached = JSON.parse(raw) as { text: string; savedAt: number };
      const serverUpdatedAt = vote ? new Date(vote.updated_at).getTime() : 0;

      // Use cached text only if it's newer than server
      if (cached.savedAt > serverUpdatedAt) {
        return cached.text;
      }
    } catch {
      // Legacy format (plain string) - clear it
      localStorage.removeItem(reviewStorageKey);
    }

    return vote?.review ?? "";
  };

  const hasCachedReview = (): boolean => {
    const raw = localStorage.getItem(reviewStorageKey);
    if (!raw) return false;

    try {
      const cached = JSON.parse(raw) as { text: string; savedAt: number };
      const serverUpdatedAt = vote ? new Date(vote.updated_at).getTime() : 0;
      return cached.savedAt > serverUpdatedAt && cached.text.length > 0;
    } catch {
      return false;
    }
  };

  const [voteSaved, setVoteSaved] = useState<"unsaved" | "saving" | "saved">(
    "unsaved"
  );
  const [madeReviewTextChange, setMadeReviewTextChange] = useState(false);
  const [reviewText, setReviewText] = useState(getInitialReviewText);
  const [voteType, setVoteType] = useState<"review" | "normal">(() => {
    return (vote && vote.review) || hasCachedReview() ? "review" : "normal";
  });
  const [guess, setGuess] = useState(vote?.guess || "");
  const [categoryDescription, setCategoryDescription] = useState("");
  const [savedReview, setSavedReview] = useState(
    !!(vote && vote.review && vote.review.length > 0)
  );
  const [category, setCategory] = useState("");

  const relevantCategories = useMemo(
    () => categories.filter((c) => c.type === entry.type)[0],
    [categories, entry.type]
  );

  useEffect(() => {
    const beforeUnload = (e: BeforeUnloadEvent) => {
      if (reviewText.length > 0 && madeReviewTextChange) {
        const confirmationMessage =
          "Are you sure? Your unsaved review will not be saved.";
        (e || window.event).returnValue = confirmationMessage;
        return confirmationMessage;
      }
      return undefined;
    };

    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [madeReviewTextChange, reviewText]);

  const mouseOverCategory = (cat: string) => {
    let categoryDescriptionLocal = "";
    switch (cat) {
      case "production":
        categoryDescriptionLocal =
          "How well produced is this song? Are the elements balanced? Is the bass nice and bassy, are the highs crisp, etc? Is the song easy to listen to multiple times without being tiring?";
        break;
      case "fits request impressively":
        categoryDescriptionLocal =
          "How well does this song go above and beyond in doing what the Secret Santa asked for?";
        break;
      case "atmosphere":
        categoryDescriptionLocal =
          "How effectively does this piece use reverb?";
        break;
      case "replay":
        categoryDescriptionLocal =
          "How often did you replay this track, just because you wanted to?";
        break;
      case "sounddesign":
      case "sound design":
        categoryDescriptionLocal =
          "Does the sound of the instruments work well with the melodies, rhythms, and progressions? In an orchestral or acoustic track, does the choice of sounds fit the piece?";
        break;
      case "fits request":
        categoryDescriptionLocal =
          "How well does the song fit the secret santa request?";
        break;
      case "memery":
        categoryDescriptionLocal = "How much of a meme is this track?";
        break;
      case "emotion":
        categoryDescriptionLocal = "Does this song make you feel?";
        break;
      case "composition":
        categoryDescriptionLocal =
          "How well is the song composed? Are the chord progressions intriguing? Is the melody evocative?";
        break;
      case "structure":
      case "arrangement":
        categoryDescriptionLocal =
          "Does this song stay fresh? Are the transitions well executed and effective? Do the different sections work well together? Are there even different sections?";
        break;
      case "uniqueness":
      case "creativity":
        categoryDescriptionLocal =
          "Have you heard songs like this before? Does this song do something new and interesting?";
        break;
      case "theme":
        categoryDescriptionLocal = "How well does this song fit the theme?";
        break;
      case "overall":
        categoryDescriptionLocal = "Do you, personally, like this song?";
        break;
    }
    setCategoryDescription(categoryDescriptionLocal);
    setCategory(cat);
  };

  const submit = async (): Promise<void> => {
    for (let i = 0; i < votesRef.current.length; i++) {
      const obj = votesRef.current[i];
      if (!obj) continue;
      if (obj.value === "") {
        alert(
          `You haven't selected a value for ${(relevantCategories as any)["category_" + (i + 1)]}`
        );
        return;
      }
    }

    if (voteType === "review" && reviewText.length > 0 && reviewText.length < 100) {
      alert(
        "Please try to write a more in-depth review! If you can't think of enough to say, there's no shame an an ordinary vote."
      );
      return;
    }

    setVoteSaved("saving");

    const reviewContent = voteType === "review" ? reviewText : "";

    const result = await api.post<"/api/compo/:compo_id/judge/:entry_id">(
      `/api/compo/${compo.id}/judge/${entry.id}`,
      {
        category_0: Number(
          votesRef.current[0] ? votesRef.current[0].value : undefined
        ),
        category_1: Number(
          votesRef.current[1] ? votesRef.current[1].value : undefined
        ),
        category_2: Number(
          votesRef.current[2] ? votesRef.current[2].value : undefined
        ),
        category_3: Number(
          votesRef.current[3] ? votesRef.current[3].value : undefined
        ),
        category_4: Number(
          votesRef.current[4] ? votesRef.current[4].value : undefined
        ),
        category_5: Number(
          votesRef.current[5] ? votesRef.current[5].value : undefined
        ),
        category_6: Number(
          votesRef.current[6] ? votesRef.current[6].value : undefined
        ),
        category_7: Number(
          votesRef.current[7] ? votesRef.current[7].value : undefined
        ),
        category_8: Number(
          votesRef.current[8] ? votesRef.current[8].value : undefined
        ),
        public: publicRef.current ? publicRef.current.checked : true,
        guess: guess,
        review: reviewContent,
      }
    );

    if (api.isSuccessToast(result)) {
      setVoteSaved("saved");
    } else {
      setVoteSaved("unsaved");
    }

    if (!api.isErrorToast(result)) {
      setMadeReviewTextChange(false);
      localStorage.removeItem(reviewStorageKey);
      if (onVote && result.data) {
        onVote(result.data);
      }
      if (reviewContent !== "") {
        setSavedReview(true);
      }
    }
  };

  if (user && entry.confirmedUserIds.indexOf(user.id) !== -1) {
    return (
      <div className="text-lg text-slate-600 dark:text-slate-300">
        That's your own song. Nice try, but no voting on that one.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Prompt
        when={madeReviewTextChange && voteType === "review" && reviewText.length > 0}
        message={() =>
          `You seem to be in the middle of writing a review. Are you sure you want to navigate away?`
        }
      />

      {entry.type === "art" && (
        <img src={entry.uploaded_url} className="rounded-lg" />
      )}

      {/* Author's Description */}
      <div>
        <h4 className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
          Author's Description
        </h4>
        <div
          className="prose rounded-lg bg-slate-100 p-3 text-sm text-slate-600 dark:bg-slate-700 dark:text-slate-300"
          dangerouslySetInnerHTML={{
            __html: markdown.toHTML(entry.description || ""),
          }}
        />
      </div>

      {/* Secret Santa Request */}
      {entry.secret_santa_prompt_text && (
        <div>
          <h4 className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
            Secret Santa request
          </h4>
          <div className="prose rounded-lg bg-slate-100 p-3 text-sm text-slate-600 dark:bg-slate-700 dark:text-slate-300">
            {entry.secret_santa_prompt_text}
          </div>
        </div>
      )}

      {entry.mod_comments && (
        <div>
          <h4 className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
            Mod Comments
          </h4>
          <div className="text-sm text-slate-500 dark:text-slate-400">
            {entry.mod_comments}
          </div>
        </div>
      )}

      {/* Vote Type Selection */}
      {!savedReview && (
        <div className="mt-6 flex items-center gap-2">
          <SmallButton
            onClick={() => setVoteType("normal")}
            variant={voteType === "normal" ? "default" : "tertiary"}
          >
            Vote +<GoldSilver coins={2} />
          </SmallButton>
          <SmallButton
            onClick={() => setVoteType("review")}
            variant={voteType === "review" ? "default" : "tertiary"}
          >
            Public Review +<GoldSilver coins={bounty} />
          </SmallButton>
        </div>
      )}

      {savedReview && (
        <div className="text-sm font-medium text-slate-600 dark:text-slate-400">
          Public Review
        </div>
      )}

      {/* Rating Categories */}
      <div className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800/50">
        <div className="flex gap-4">
          <div className="flex-1">
            {relevantCategories.list.map((categoryName, i) => {
              if (
                categoryName.toLowerCase() === "theme" &&
                !entry.followed_theme
              ) {
                return null;
              }
              return (
                <VoteWidget
                  key={entry.id * 100 + i}
                  inputref={(r) => (votesRef.current[i] = r)}
                  category={categoryName}
                  category_id={i}
                  maxScore={5}
                  mouseOverCategory={(cat) => mouseOverCategory(cat)}
                  initialScore={(vote && vote.votes[i]) || undefined}
                />
              );
            })}
          </div>

          <div className="flex-1 border-l border-slate-200 pl-4 dark:border-slate-700">
            {category ? (
              <>
                <h4 className="mb-1 text-sm font-semibold capitalize text-slate-700 dark:text-slate-300">
                  {category}
                </h4>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {categoryDescription}
                </p>
              </>
            ) : (
              <p className="text-sm italic text-slate-400 dark:text-slate-500">
                Hover over a category to see its description
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Review Textarea */}
      {voteType === "review" && (
        <textarea
          name="review"
          onChange={(e) => {
            const value = e.target.value;
            setReviewText(value);
            setMadeReviewTextChange(true);
            localStorage.setItem(
              reviewStorageKey,
              JSON.stringify({ text: value, savedAt: Date.now() })
            );
          }}
          value={reviewText}
          className="min-h-[150px] w-full rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-700 placeholder-slate-400 focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:placeholder-slate-500"
          ref={(review) => {
            if (review) reviewRef.current = review;
          }}
          placeholder="Leave a review!&#10;&#10;You must say at least a few things that you liked about the piece, and talk about how you would improve the piece (if you can think of anything).&#10;&#10;Go for quality! Reviews that are exceptionally good may earn even more gold.&#10;&#10;Review scores are weighted doubly and made public after the compo."
        />
      )}

      {/* Make Vote Public Checkbox */}
      {voteType === "normal" && (
        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
          <input
            type="checkbox"
            defaultChecked={vote && vote.public}
            ref={(input) => {
              if (input) publicRef.current = input;
            }}
            name="public"
            className="h-4 w-4 rounded border-slate-300 text-sky-500 focus:ring-sky-400 dark:border-slate-600 dark:bg-slate-700"
          />
          Make your vote public after the contest?
        </label>
      )}

      {/* Guess Input */}
      <UserSearchInput
        defaultValue={guess}
        placeholder={`Optional: Guess who wrote this ${entry.type}!`}
        onValueChange={setGuess}
      />

      {/* Actions Footer */}
      <div className="-mx-4 -mb-4 flex items-center gap-4 rounded-b-lg bg-slate-100 px-4 py-3 dark:bg-slate-700/50">
        <SmallButton
          onClick={() => submit()}
          variant="primary"
          testId={TestIds.JudgeVoteSubmitButton}
          disabled={voteSaved === "saving"}
        >
          {voteSaved === "saving" ? "Saving..." : "Save Vote"}
        </SmallButton>

        <SmallButton to={`/entry/${entry.id}`} variant="tertiary">
          Go to entry
        </SmallButton>

        {voteSaved === "saved" && (
          <span className="text-sm text-emerald-600 dark:text-emerald-400">
            Saved!
          </span>
        )}
      </div>
    </div>
  );
};
